// ============================================================================
//  HR Module — Employés
//  EmployeesPage · EmployeeDetailPage · EmployeeCreatePage
// ============================================================================

import React, { useState, useEffect } from 'react'
import { usersApi } from '@/modules/users/api/users.api'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Users,
  Plus,
  Search,
  Filter,
  Download,
  MoreVertical,
  ChevronLeft,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Building2,
  UserCheck,
  UserX,
  AlertTriangle,
  TrendingUp,
  Clock,
  FileText,
  Award,
  Activity,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  ArrowRight,
  MapPin,
  Hash,
} from 'lucide-react'
import { hrApi, type Employee } from '@/modules/hr/api/hr.api'
import { adminApi } from '@/modules/admin/api/admin.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'
import { usePermission } from '@/modules/auth/hooks/usePermission'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.round(n))

// Convertit Decimal.js Prisma { s, e, d } ou number/string en number JS
const toNum = (v: any): number => {
  if (v === null || v === undefined) return 0
  if (typeof v === 'number') return v
  if (typeof v === 'string') return parseFloat(v) || 0
  if (typeof v === 'object' && Array.isArray(v.d) && typeof v.e === 'number') {
    // Decimal.js: reconstruire la valeur depuis d et e
    const str = v.d.join('')
    const intPart = str.slice(0, v.e + 1) || '0'
    const decPart = str.slice(v.e + 1)
    return parseFloat((v.s < 0 ? '-' : '') + intPart + (decPart ? '.' + decPart : '')) || 0
  }
  return Number(v) || 0
}

const fmtXOF = (n: any) =>
  new Intl.NumberFormat('fr-SN', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0,
  }).format(Math.round(toNum(n)))

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })

const STATUS_CONFIG = {
  ACTIVE: {
    label: 'Actif',
    color: 'text-[#059669]',
    bg: 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)]',
    dot: 'bg-[#10B981]',
  },
  ON_LEAVE: {
    label: 'En congé',
    color: 'text-[#D97706]',
    bg: 'bg-[#FEF3C7] border-[rgba(245,158,11,0.25)]',
    dot: 'bg-[#F59E0B]',
  },
  SUSPENDED: {
    label: 'Suspendu',
    color: 'text-[#EA580C]',
    bg: 'bg-[#FFF7ED] border-[rgba(249,115,22,0.25)]',
    dot: 'bg-[#F97316]',
  },
  TERMINATED: {
    label: 'Terminé',
    color: 'text-[var(--tx3)]',
    bg: 'bg-[#D6D2EC]/10 border-[var(--tx3)]/20',
    dot: 'bg-[var(--tx3)]',
  },
} as const

// ─── Composants partagés ─────────────────────────────────────────────────────

function StatusBadge({ status }: { status: keyof typeof STATUS_CONFIG }) {
  const cfg = STATUS_CONFIG[status]
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  )
}

function Avatar({
  employee,
  size = 'md',
}: {
  employee: Pick<Employee, 'firstName' | 'lastName' | 'avatarUrl'>
  size?: 'sm' | 'md' | 'lg'
}) {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-16 h-16 text-xl' }
  const initials = `${employee.firstName[0]}${employee.lastName[0]}`
  return employee.avatarUrl ? (
    <img
      src={employee.avatarUrl}
      alt={initials}
      className={`${sizes[size]} rounded-full object-cover`}
    />
  ) : (
    <div
      className={`${sizes[size]} rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] font-bold shrink-0`}
    >
      {initials}
    </div>
  )
}

// ============================================================================
//  EMPLOYEES PAGE
// ============================================================================

export default function EmployeesPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [deptId, setDeptId] = useState('')
  const [page, setPage] = useState(1)
  const addNotification = useNotificationStore((s) => s.addNotification)

  const [showExpiryAlerts, setShowExpiryAlerts] = useState(false)

  const { data: expiryAlerts } = useQuery({
    queryKey: ['contractExpiryAlerts'],
    queryFn: () => hrApi.contracts.getExpiryAlerts(),
    enabled: showExpiryAlerts,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['employees', { search, status, deptId, page }],
    queryFn: () =>
      hrApi.employees.list({
        search: search || undefined,
        status: status || undefined,
        departmentId: deptId || undefined,
        page,
        limit: 20,
      }),
    placeholderData: (prev) => prev,
  })

  const { data: depts } = useQuery({
    queryKey: ['departments'],
    queryFn: () => hrApi.employees.departments(),
  })

  const employees = data?.employees ?? []
  const stats = data?.stats
  const total = data?.total ?? 0

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Employés</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            {fmt(total)} employé{total > 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/hr/employees/new"
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouvel employé
        </Link>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
          {[
            {
              label: 'Actifs',
              value: fmt(stats.totalActive),
              icon: UserCheck,
              color: 'bg-[#10B981]/10 text-[#059669]',
            },
            {
              label: 'Arrivées ce mois',
              value: `+${stats.newThisMonth}`,
              icon: TrendingUp,
              color: 'bg-[#E0F2FE] text-[var(--blue-d)]',
            },
            {
              label: 'En congé',
              value: fmt(stats.onLeave),
              icon: Clock,
              color: 'bg-[#FEF3C7] text-[#D97706]',
            },
            {
              label: 'Contrats à renouveler',
              value: fmt(stats.expiringContracts),
              icon: AlertTriangle,
              color: 'bg-[#FEF2F2] text-[#DC2626]',
              onClick: () => setShowExpiryAlerts(true),
            },
          ].map((k) => (
            <div
              key={k.label}
              onClick={(k as any).onClick}
              className={"bg-white border border-[var(--bd)] rounded-xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow" + ((k as any).onClick ? " cursor-pointer" : "")}
              style={{
                borderTop: `3px solid ${k.color.match(/#[0-9A-Fa-f]{3,6}/g)?.[0] ?? 'var(--brand-orange)'}`,
              }}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${k.color}`}>
                <k.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[var(--brand-dark)] leading-none">{k.value}</p>
                <p className="text-xs text-[var(--tx3)]">{k.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Rechercher nom, matricule, poste…"
            className="w-full pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select
          value={deptId}
          onChange={(e) => {
            setDeptId(e.target.value)
            setPage(1)
          }}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        >
          <option value="">Tous les départements</option>
          {depts?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      {/* Liste */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-[var(--tx3)]">
            <Users className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">Aucun employé trouvé</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Employé', 'Département', 'Poste', "Date d'entrée", 'Statut', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {employees.map((emp) => (
                <tr key={emp.id} className="hover:bg-[var(--surface-2)] transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar employee={emp} size="sm" />
                      <div>
                        <p className="text-sm font-medium text-[var(--brand-dark)]">
                          {emp.firstName} {emp.lastName}
                        </p>
                        <p className="text-xs text-[var(--tx3)]">{emp.matricule}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--tx2)]">
                    {emp.department?.name ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--tx2)]">{emp.jobTitle}</td>
                  <td className="px-4 py-3 text-sm text-[var(--tx3)]">{fmtDate(emp.hireDate)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={emp.status as any} />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/hr/employees/${emp.id}`}
                      className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-[var(--blue-d)] hover:text-blue-300 transition-all"
                    >
                      Voir <ArrowRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--bd)]">
            <p className="text-xs text-[var(--tx3)]">
              {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} sur {fmt(total)}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded bg-[var(--surface-2)] text-sm text-[var(--brand-dark)] disabled:opacity-40 hover:bg-[var(--bd)] transition-colors"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * 20 >= total}
                className="px-3 py-1 rounded bg-[var(--surface-2)] text-sm text-[var(--brand-dark)] disabled:opacity-40 hover:bg-[var(--bd)] transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  EMPLOYEE DETAIL PAGE
// ============================================================================

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const [tab, setTab] = useState<
    'info' | 'contracts' | 'payslips' | 'leaves' | 'career' | 'account'
  >('info')

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => hrApi.employees.get(id!),
    enabled: !!id,
  })

  const { data: contracts } = useQuery({
    queryKey: ['contracts', id],
    queryFn: () => hrApi.contracts.listByEmployee(id!),
    enabled: tab === 'contracts' && !!id,
  })

  const { data: payslipsData } = useQuery({
    queryKey: ['payslips', id],
    queryFn: () => hrApi.payroll.getEmployeePayslips(id!),
    enabled: tab === 'payslips' && !!id,
  })

  const { data: leavesData } = useQuery({
    queryKey: ['leaves', id],
    queryFn: () => hrApi.leaves.list({ employeeId: id }),
    enabled: tab === 'leaves' && !!id,
  })

  const { data: balances } = useQuery({
    queryKey: ['leaveBalances', id],
    queryFn: () => hrApi.leaves.getBalances(id!),
    enabled: tab === 'leaves' && !!id,
  })

  const { data: history } = useQuery({
    queryKey: ['careerHistory', id],
    queryFn: () => hrApi.career.getCareerHistory(id!),
    enabled: tab === 'career' && !!id,
  })

  // ── Compte utilisateur lié ────────────────────────────────────────────────
  const { data: linkedUser, refetch: refetchUser } = useQuery({
    queryKey: ['employee-linked-user', id],
    queryFn: async () => {
      if (!employee?.userId) return null
      try {
        return await adminApi.getUserById(employee.userId)
      } catch {
        return null
      }
    },
    enabled: !!employee?.userId,
  })

  const [showContractModal, setShowContractModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showDeleteEmpModal, setShowDeleteEmpModal] = useState(false)
  // Suppression définitive : SUPER_ADMIN uniquement, confirmation par saisie du matricule
  const { isSuperAdmin } = usePermission()
  const [confirmMatricule, setConfirmMatricule] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [pwError, setPwError] = useState('')

  // Auto-ouverture du modal "Créer contrat" après la création de l'employé
  // Déclenché via redirection depuis EmployeeCreatePage avec ?openContract=1
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('openContract') === '1' && employee) {
      setTab('contracts')
      setShowContractModal(true)
      // Nettoyer le query param pour éviter re-déclenchement sur refresh
      searchParams.delete('openContract')
      setSearchParams(searchParams, { replace: true })
    }
  }, [employee, searchParams, setSearchParams])

  const toggleUserStatus = useMutation({
    mutationFn: () =>
      linkedUser?.isActive
        ? adminApi.deactivateUser(employee!.userId!)
        : adminApi.activateUser(employee!.userId!),
    onSuccess: () => {
      refetchUser()
      queryClient.invalidateQueries({ queryKey: ['employee', id] })
      addNotification({ type: 'success', title: 'Statut mis à jour', message: '' })
    },
    onError: () =>
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Impossible de modifier le statut',
      }),
  })

  const resetPassword = useMutation({
    mutationFn: () => {
      if (newPassword.length < 8) {
        setPwError('Minimum 8 caractères')
        throw new Error('')
      }
      if (!/[A-Z]/.test(newPassword)) {
        setPwError('Au moins une majuscule')
        throw new Error('')
      }
      if (!/[0-9]/.test(newPassword)) {
        setPwError('Au moins un chiffre')
        throw new Error('')
      }
      setPwError('')
      return adminApi.resetUserPassword(employee!.userId!, newPassword)
    },
    onSuccess: () => {
      setShowResetModal(false)
      setNewPassword('')
      addNotification({
        type: 'success',
        title: 'Mot de passe réinitialisé',
        message: "L'utilisateur devra changer son mot de passe à la prochaine connexion.",
      })
    },
    onError: (err: any) => {
      if (err.message) return
      setPwError(err?.response?.data?.message ?? 'Erreur')
    },
  })

  const deleteUserAccount = useMutation({
    mutationFn: () => adminApi.deleteUser(employee!.userId!),
    onSuccess: () => {
      setShowDeleteModal(false)
      queryClient.invalidateQueries({ queryKey: ['employee', id] })
      queryClient.invalidateQueries({ queryKey: ['employee-linked-user', id] })
      addNotification({
        type: 'success',
        title: 'Compte supprimé',
        message: 'La fiche employé est conservée.',
      })
    },
    onError: () =>
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Impossible de supprimer le compte',
      }),
  })

  const [contractForm, setContractForm] = useState({
    type: 'CDI' as 'CDI' | 'CDD' | 'INTERNSHIP' | 'FREELANCE' | 'TEMPORARY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    trialEndDate: '',
    grossSalary: '',
    workingHours: '40',
    // [Payroll]
    isCadre: false,
    category: '',
    daysPerMonth: 30,
    paymentMethod: 'BANK_TRANSFER' as 'BANK_TRANSFER' | 'CHECK' | 'CASH',
    bankAccountIban: '',
  })
  const setC = (k: string, v: any) => setContractForm(f => ({ ...f, [k]: v }))

  const createContract = useMutation({
    mutationFn: () => hrApi.contracts.create({
      employeeId:   id!,
      type:         contractForm.type,
      startDate:    contractForm.startDate,
      endDate:      contractForm.endDate || undefined,
      trialEndDate: contractForm.trialEndDate || undefined,
      grossSalary:  parseFloat(contractForm.grossSalary),
      workingHours: contractForm.workingHours ? parseInt(contractForm.workingHours) : undefined,
      // [Payroll]
      isCadre:         contractForm.isCadre,
      category:        contractForm.category || undefined,
      daysPerMonth:    contractForm.daysPerMonth,
      paymentMethod:   contractForm.paymentMethod,
      bankAccountIban: contractForm.bankAccountIban || undefined,
    } as any),
    onSuccess: () => {
      setShowContractModal(false)
      setContractForm({
        type: 'CDI', startDate: new Date().toISOString().split('T')[0],
        endDate: '', trialEndDate: '', grossSalary: '', workingHours: '40',
        isCadre: false, category: '', daysPerMonth: 30,
        paymentMethod: 'BANK_TRANSFER', bankAccountIban: '',
      })
      queryClient.invalidateQueries({ queryKey: ['contracts', id] })
      addNotification({ type: 'success', title: 'Contrat créé', message: 'Le contrat a été créé avec succès.' })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message ?? 'Erreur création contrat' }),
  })

  const { data: allUsers } = useQuery({
    queryKey: ['admin-users-for-link'],
    queryFn: () => usersApi.directory({ limit: 100 }),
    enabled: showLinkModal,
  })

  const linkExistingUser = useMutation({
    mutationFn: () => hrApi.employees.update(id!, { userId: linkUserId }),
    onSuccess: () => {
      setShowLinkModal(false)
      setLinkUserId('')
      queryClient.invalidateQueries({ queryKey: ['employee', id] })
      queryClient.invalidateQueries({ queryKey: ['employee-linked-user', id] })
      addNotification({
        type: 'success',
        title: 'Compte lié',
        message: 'Le compte utilisateur a été associé.',
      })
    },
    onError: () =>
      addNotification({ type: 'error', title: 'Erreur', message: 'Impossible de lier le compte' }),
  })

  const deleteEmployee = useMutation({
    // Le compte utilisateur lié est toujours supprimé avec la fiche (repli : désactivé)
    mutationFn: () => hrApi.employees.delete(id!, true),
    onSuccess: (res: any) => {
      setShowDeleteEmpModal(false)
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      const msg = res?.userDeleted
        ? 'Employé, historique et compte utilisateur supprimés définitivement.'
        : res?.userDeactivated
          ? `Employé et historique supprimés. Le compte utilisateur a été désactivé (contenu lié non supprimable : ${res?.userBlocker ?? 'données liées'}).`
          : 'Employé et historique supprimés définitivement.'
      addNotification({ type: 'success', title: 'Employé supprimé', message: msg })
      navigate('/hr/employees')
    },
    onError: (err: any) =>
      addNotification({
        type: 'error',
        title: 'Suppression impossible',
        message: err?.response?.data?.message ?? 'Impossible de supprimer cet employé',
      }),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }
  if (!employee) return null

  const TABS = [
    { key: 'info', label: 'Informations' },
    { key: 'contracts', label: 'Contrats' },
    { key: 'payslips', label: 'Bulletins' },
    { key: 'leaves', label: 'Congés' },
    { key: 'career', label: 'Carrière' },
    { key: 'account', label: employee?.userId ? '🔐 Compte' : '🔐 Compte' },
  ] as const

  const CONTRACT_STATUS: Record<string, { label: string; color: string }> = {
    DRAFT: { label: 'Brouillon', color: 'text-[var(--tx3)]' },
    CONFIRMED: { label: 'Confirmé', color: 'text-[var(--blue-d)]' },
    TRIAL_PERIOD: { label: 'Période essai', color: 'text-[#D97706]' },
    ACTIVE: { label: 'Actif', color: 'text-[#059669]' },
    EXPIRED: { label: 'Expiré', color: 'text-[#DC2626]' },
    TERMINATED: { label: 'Résilié', color: 'text-[var(--tx3)]' },
  }

  const LEAVE_STATUS: Record<string, { label: string; color: string }> = {
    PENDING: { label: 'En attente', color: 'text-[#D97706]' },
    MANAGER_APPROVED: { label: 'Manager approuvé', color: 'text-[var(--blue-d)]' },
    APPROVED: { label: 'Approuvé', color: 'text-[#059669]' },
    REJECTED: { label: 'Rejeté', color: 'text-[#DC2626]' },
    CANCELLED: { label: 'Annulé', color: 'text-[var(--tx3)]' },
  }

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <Avatar employee={employee} size="lg" />
          <div>
            <h1 className="text-2xl font-bold text-[var(--brand-dark)]">
              {employee.firstName} {employee.lastName}
            </h1>
            <p className="text-[var(--tx3)]">{employee.jobTitle}</p>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-[var(--tx3)]">{employee.matricule}</span>
              <StatusBadge status={employee.status as any} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/hr/employees/${id}/edit`}
            className="flex items-center gap-2 px-3 py-2 bg-[var(--surface-2)] hover:bg-[var(--bd)] text-[var(--brand-dark)] rounded-lg text-sm transition-colors"
          >
            <Edit className="w-4 h-4" />
            Modifier
          </Link>
          {isSuperAdmin && (
            <button
              onClick={() => {
                setConfirmMatricule('')
                setShowDeleteEmpModal(true)
              }}
              className="flex items-center gap-2 px-3 py-2 bg-[#FEF2F2] hover:bg-red-100 text-[#DC2626] border border-red-200 rounded-lg text-sm transition-colors"
              title="Suppression définitive — super-administrateur uniquement"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer définitivement
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--bd)] pb-0">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-500 text-[var(--blue-d)]'
                : 'border-transparent text-[var(--tx3)] hover:text-[var(--brand-dark)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Informations personnelles</h3>
            {[
              { icon: Mail, label: 'Email', value: employee.email },
              { icon: Phone, label: 'Téléphone', value: employee.phone ?? '—' },
              { icon: Calendar, label: "Date d'embauche", value: fmtDate(employee.hireDate) },
              { icon: Building2, label: 'Département', value: employee.department?.name ?? '—' },
              { icon: Hash, label: 'Matricule', value: employee.matricule },
              { icon: Hash, label: 'NINEA', value: employee.ninea ?? '—' },
              { icon: Hash, label: 'N° IPRES', value: employee.ipresId ?? '—' },
              {
                icon: Users,
                label: 'Statut familial',
                value: ((s: string | undefined) => {
                  const labels: Record<string, string> = {
                    CELIBATAIRE: 'Célibataire', MARIE: 'Marié(e)',
                    DIVORCE: 'Divorcé(e)', VEUF: 'Veuf/Veuve', UNION_LIBRE: 'Union libre',
                  }
                  return s ? labels[s] ?? s : '—'
                })(employee.maritalStatus),
              },
              {
                icon: Users,
                label: 'Enfants à charge',
                value: String(employee.dependentChildren ?? 0),
              },
              {
                icon: Hash,
                label: 'Parts fiscales',
                value: employee.fiscalParts != null
                  ? `${employee.fiscalParts} part${employee.fiscalParts > 1 ? 's' : ''}`
                  : '—',
              },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#F4F2FB] flex items-center justify-center shrink-0">
                  <row.icon className="w-4 h-4 text-[var(--tx3)]" />
                </div>
                <div>
                  <p className="text-xs text-[var(--tx3)]">{row.label}</p>
                  <p className="text-sm text-[var(--brand-dark)]">{row.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {/* Manager */}
            {employee.manager && (
              <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-3">Manager</h3>
                <Link
                  to={`/hr/employees/${employee.managerId}`}
                  className="flex items-center gap-3 hover:bg-[var(--surface-2)] rounded-lg p-2 -mx-2 transition-colors"
                >
                  <Avatar employee={employee.manager as any} size="sm" />
                  <span className="text-sm text-[var(--blue-d)]">
                    {(employee.manager as any).firstName} {(employee.manager as any).lastName}
                  </span>
                  <ArrowRight className="w-4 h-4 text-[var(--tx3)] ml-auto" />
                </Link>
              </div>
            )}

            {/* Actions rapides */}
            <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-3">Actions rapides</h3>
              <div className="space-y-2">
                {[
                  { label: 'Voir les congés', to: `/hr/leaves?employeeId=${id}`, icon: Calendar },
                  { label: 'Voir les bulletins', to: `/hr/payroll?employee=${id}`, icon: FileText },
                  {
                    label: 'Historique carrière',
                    onClick: () => setTab('career'),
                    icon: TrendingUp,
                  },
                ].map((a, i) =>
                  a.to ? (
                    <Link
                      key={i}
                      to={a.to}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-[var(--surface-2)] rounded-lg text-sm text-[var(--tx2)] hover:text-[var(--brand-dark)] transition-colors"
                    >
                      <a.icon className="w-4 h-4 text-[var(--tx3)]" />
                      {a.label}
                      <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100" />
                    </Link>
                  ) : (
                    <button
                      key={i}
                      onClick={a.onClick}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--surface-2)] rounded-lg text-sm text-[var(--tx2)] hover:text-[var(--brand-dark)] transition-colors text-left"
                    >
                      <a.icon className="w-4 h-4 text-[var(--tx3)]" />
                      {a.label}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'contracts' && (
        <div className="space-y-4">
          {/* Header avec bouton créer */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--tx3)]">{contracts?.contracts?.length ?? 0} contrat(s)</p>
            <button
              onClick={() => setShowContractModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouveau contrat
            </button>
          </div>

          {/* Liste */}
          {(contracts?.contracts ?? []).map((c) => (
            <div key={c.id} className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-semibold text-[var(--brand-dark)]">{c.type}</span>
                    <span className={`text-xs font-medium ${CONTRACT_STATUS[c.status]?.color}`}>
                      {CONTRACT_STATUS[c.status]?.label}
                    </span>
                    {c.isCadre && (
                      <span className="text-[10px] px-2 py-0.5 bg-[#EDE9FE] text-[#7C3AED] rounded font-medium">
                        Cadre
                      </span>
                    )}
                    {c.category && (
                      <span className="text-[10px] px-2 py-0.5 bg-[var(--surface-2)] text-[var(--tx2)] rounded font-medium">
                        {c.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--tx2)] mb-1">{c.position}</p>
                  <div className="flex gap-4 text-xs text-[var(--tx3)]">
                    <span>Du {fmtDate(c.startDate)}</span>
                    {c.endDate && <span>au {fmtDate(c.endDate)}</span>}
                    <span>· Renouvellements : {c.renewalCount}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[var(--brand-dark)]">{fmtXOF(c.grossSalary)}</p>
                  <p className="text-xs text-[var(--tx3)]">Salaire brut/mois</p>
                </div>
              </div>
              {/* [Phase 5] Bouton vers la page composants salaire */}
              {(c.status === 'ACTIVE' || c.status === 'CONFIRMED' || c.status === 'TRIAL_PERIOD') && (
                <div className="mt-3 pt-3 border-t border-[var(--bd)] flex justify-end">
                  <Link
                    to={`/hr/contracts/${c.id}/components`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--surface-2)] hover:bg-[var(--blue)]/10 hover:text-[var(--blue-d)] text-xs font-medium text-[var(--tx2)] rounded-lg transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Configurer les composants salaire
                  </Link>
                </div>
              )}
            </div>
          ))}
          {!contracts?.contracts?.length && (
            <div className="flex flex-col items-center justify-center h-32 text-[var(--tx3)] gap-2">
              <FileText className="w-8 h-8 opacity-30" />
              <p className="text-sm">Aucun contrat — créez le premier</p>
            </div>
          )}

          {/* Modal création contrat */}
          {showContractModal && (
            <div className="fixed inset-0 bg-[rgba(22,12,38,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
              <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
                style={{ boxShadow:'0 24px 64px rgba(22,12,38,0.18)' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bd)]">
                  <h3 className="text-base font-bold text-[var(--brand-dark)]">Nouveau contrat</h3>
                  <button onClick={() => setShowContractModal(false)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">✕</button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  {/* Type */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Type de contrat *</label>
                    <select value={contractForm.type} onChange={e => setC('type', e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]">
                      <option value="CDI">CDI — Contrat à Durée Indéterminée</option>
                      <option value="CDD">CDD — Contrat à Durée Déterminée</option>
                      <option value="INTERNSHIP">Stage</option>
                      <option value="FREELANCE">Freelance / Prestataire</option>
                      <option value="TEMPORARY">Intérim</option>
                    </select>
                  </div>

                  {/* Salaire */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Salaire brut mensuel (XOF) *</label>
                    <input type="number" value={contractForm.grossSalary} onChange={e => setC('grossSalary', e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
                      placeholder="ex : 500000" />
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Date de début *</label>
                      <input type="date" value={contractForm.startDate} onChange={e => setC('startDate', e.target.value)}
                        className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
                    </div>
                    {contractForm.type !== 'CDI' && (
                      <div>
                        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Date de fin</label>
                        <input type="date" value={contractForm.endDate} onChange={e => setC('endDate', e.target.value)}
                          className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
                      </div>
                    )}
                  </div>

                  {/* Période d'essai */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Fin période d'essai</label>
                    <input type="date" value={contractForm.trialEndDate} onChange={e => setC('trialEndDate', e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
                  </div>

                  {/* Heures */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Heures hebdomadaires</label>
                    <input type="number" value={contractForm.workingHours} onChange={e => setC('workingHours', e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
                      placeholder="40" />
                  </div>

                  {/* ── Bloc paramètres paie ── */}
                  <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">Paramètres paie</p>
                      <span className="text-[10px] px-2 py-0.5 bg-[#EDE9FE] text-[#7C3AED] rounded font-medium">Sénégal</span>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-[var(--tx2)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={contractForm.isCadre}
                        onChange={e => setC('isCadre', e.target.checked)}
                        className="w-4 h-4"
                      />
                      Cadre
                      <span className="text-xs text-[var(--tx3)]">— ajoute IPRES Régime Complémentaire</span>
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Catégorie professionnelle</label>
                        <select value={contractForm.category} onChange={e => setC('category', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]">
                          <option value="">— Aucune —</option>
                          <option value="1ère">1ère catégorie</option>
                          <option value="2ème">2ème catégorie</option>
                          <option value="3ème">3ème catégorie</option>
                          <option value="4ème">4ème catégorie</option>
                          <option value="5ème">5ème catégorie</option>
                          <option value="6ème">6ème catégorie</option>
                          <option value="7ème A">7ème A (cadre)</option>
                          <option value="7ème B">7ème B (cadre supérieur)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Jours par mois</label>
                        <select value={contractForm.daysPerMonth} onChange={e => setC('daysPerMonth', parseInt(e.target.value))}
                          className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]">
                          <option value={30}>30 (mensualisé)</option>
                          <option value={26}>26 (jours ouvrables)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Mode de paiement</label>
                      <select value={contractForm.paymentMethod} onChange={e => setC('paymentMethod', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]">
                        <option value="BANK_TRANSFER">Virement bancaire</option>
                        <option value="CHECK">Chèque</option>
                        <option value="CASH">Espèces</option>
                      </select>
                    </div>

                    {contractForm.paymentMethod === 'BANK_TRANSFER' && (
                      <div>
                        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">IBAN / RIB</label>
                        <input type="text" value={contractForm.bankAccountIban} onChange={e => setC('bankAccountIban', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
                          placeholder="SN12 K001 0000 1234 5678 9012 345" />
                      </div>
                    )}

                    <p className="text-xs text-[var(--tx3)]">
                      💡 Une fois le contrat créé, configure les <strong>composants salaire</strong> (base, transport, sursalaire…) depuis la page de détail.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 px-6 py-4 border-t border-[var(--bd)]">
                  <button onClick={() => setShowContractModal(false)}
                    className="flex-1 px-4 py-2 border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors">
                    Annuler
                  </button>
                  <button
                    onClick={() => createContract.mutate()}
                    disabled={createContract.isPending || !contractForm.grossSalary || !contractForm.startDate}
                    className="flex-1 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    {createContract.isPending ? '…' : 'Créer le contrat'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'payslips' && (
        <div className="space-y-3">
          {(payslipsData?.payslips ?? []).map((p) => (
            <Link
              key={p.id}
              to={`/hr/payroll/payslips/${p.id}`}
              className="flex items-center justify-between bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-4 hover:border-blue-500/30 transition-colors"
            >
              <div>
                <p className="text-sm font-medium text-[var(--brand-dark)]">
                  {new Date(p.period!.year, p.period!.month - 1).toLocaleDateString('fr-SN', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
                <p className="text-xs text-[var(--tx3)] capitalize">{p.status}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-[var(--brand-dark)]">{fmtXOF(p.netPayable)}</p>
                <p className="text-xs text-[var(--tx3)]">Net à payer</p>
              </div>
            </Link>
          ))}
          {!payslipsData?.payslips?.length && (
            <div className="flex items-center justify-center h-32 text-[var(--tx3)] text-sm">
              Aucun bulletin
            </div>
          )}
        </div>
      )}

      {tab === 'leaves' && (
        <div className="space-y-5 nx-fade-up">
          {/* Soldes */}
          {balances && balances.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[var(--tx3)] uppercase tracking-wider mb-3">
                Soldes {new Date().getFullYear()}
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {(balances ?? []).map((b) => (
                  <div
                    key={b.id}
                    className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: b.leaveType.color ?? '#3b82f6' }}
                      />
                      <p className="text-xs text-[var(--tx3)] font-medium">{b.leaveType.name}</p>
                    </div>
                    <p className="text-2xl font-bold text-[var(--brand-dark)]">{b.remaining}</p>
                    <p className="text-xs text-[var(--tx3)]">jours restants</p>
                    <div className="mt-2 flex justify-between text-xs text-[var(--tx3)]">
                      <span>Pris: {b.taken}j</span>
                      <span>En cours: {b.pending}j</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Demandes */}
          <div className="space-y-3">
            {(leavesData?.requests ?? []).map((r) => (
              <div
                key={r.id}
                className="bg-white border border-[var(--bd)] rounded-xl p-5 flex items-center justify-between shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: r.leaveType?.color ?? '#64748b' }}
                  />
                  <div>
                    <p className="text-sm font-medium text-[var(--brand-dark)]">{r.leaveType?.name}</p>
                    <p className="text-xs text-[var(--tx3)]">
                      {fmtDate(r.startDate)} → {fmtDate(r.endDate)} · {r.totalDays}j
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-medium ${LEAVE_STATUS[r.status]?.color}`}>
                  {LEAVE_STATUS[r.status]?.label}
                </span>
              </div>
            ))}
            {!leavesData?.requests?.length && (
              <div className="flex items-center justify-center h-32 text-[var(--tx3)] text-sm">
                Aucune demande de congé
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'account' && (
        <div className="space-y-4 max-w-2xl">
          {employee?.userId && linkedUser ? (
            <>
              {/* Infos compte */}
              <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Compte utilisateur lié</h3>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      linkedUser.isActive
                        ? 'bg-[#D1FAE5] text-[#059669]'
                        : 'bg-[#FEF2F2] text-[#DC2626]'
                    }`}
                  >
                    {linkedUser.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <dl className="space-y-3">
                  {[
                    { label: 'Email', value: linkedUser.email },
                    {
                      label: 'Rôles',
                      value:
                        linkedUser.userRoles?.map((ur: any) => ur.role?.name).join(', ') || '—',
                    },
                    {
                      label: 'Dernière connexion',
                      value: linkedUser.lastLoginAt
                        ? new Date(linkedUser.lastLoginAt).toLocaleString('fr-FR')
                        : 'Jamais connecté',
                    },
                    { label: '2FA', value: linkedUser.twoFactorEnabled ? '✅ Activée' : '—' },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="flex justify-between items-center py-1 border-b border-[#F4F2FB] last:border-0"
                    >
                      <dt className="text-xs text-[var(--tx3)]">{label}</dt>
                      <dd className="text-sm text-[var(--tx2)] font-medium">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              {/* Actions */}
              <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm space-y-3">
                <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-1">Actions sur le compte</h3>

                {/* Activer / Désactiver */}
                <div className="flex items-center justify-between p-3 bg-[var(--surface-2)] rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-[var(--tx2)]">
                      {linkedUser.isActive ? 'Désactiver le compte' : 'Réactiver le compte'}
                    </p>
                    <p className="text-xs text-[var(--tx3)] mt-0.5">
                      {linkedUser.isActive
                        ? "L'utilisateur ne pourra plus se connecter"
                        : "Restaure l'accès à la plateforme"}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleUserStatus.mutate()}
                    disabled={toggleUserStatus.isPending}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      linkedUser.isActive
                        ? 'bg-[#FEF2F2] text-[#DC2626] hover:bg-red-100 border border-red-200'
                        : 'bg-[#ECFDF5] text-[#059669] hover:bg-green-100 border border-green-200'
                    }`}
                  >
                    {toggleUserStatus.isPending
                      ? '…'
                      : linkedUser.isActive
                        ? 'Désactiver'
                        : 'Réactiver'}
                  </button>
                </div>

                {/* Reset mot de passe */}
                <div className="flex items-center justify-between p-3 bg-[var(--surface-2)] rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-[var(--tx2)]">
                      Réinitialiser le mot de passe
                    </p>
                    <p className="text-xs text-[var(--tx3)] mt-0.5">
                      L'utilisateur devra changer son mot de passe
                    </p>
                  </div>
                  <button
                    onClick={() => setShowResetModal(true)}
                    className="px-4 py-2 bg-[#EFF6FF] text-[var(--blue-d)] hover:bg-blue-100 border border-blue-200 rounded-lg text-sm font-medium transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>

                {/* Supprimer le compte */}
                <div className="flex items-center justify-between p-3 bg-[#FEF2F2] rounded-lg border border-red-100">
                  <div>
                    <p className="text-sm font-medium text-[#DC2626]">Supprimer le compte</p>
                    <p className="text-xs text-[var(--tx3)] mt-0.5">
                      La fiche employé RH sera conservée
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-[#DC2626] text-white hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white border border-[var(--bd)] rounded-xl p-10 text-center space-y-4 shadow-sm">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: 'var(--surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: 24,
                }}
              >
                🔐
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--tx2)]">Aucun compte utilisateur lié</p>
                <p className="text-xs text-[var(--tx3)] mt-1">
                  Cet employé n'a pas encore accès à la plateforme.
                </p>
              </div>
              <button
                onClick={() => (window.location.href = '/admin/users')}
                className="px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Inviter un utilisateur
              </button>
            </div>
          )}

          {/* Modal — Reset mot de passe */}
          {showResetModal && (
            <div className="fixed inset-0 bg-[rgba(22,12,38,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
              <div
                className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-4"
                style={{ boxShadow: '0 24px 64px rgba(13,8,32,0.18)' }}
              >
                <h3 className="text-base font-bold text-[var(--brand-dark)]">
                  Réinitialiser le mot de passe
                </h3>
                <p className="text-xs text-[var(--tx3)]">
                  Définissez un nouveau mot de passe temporaire pour{' '}
                  <strong>{linkedUser?.email}</strong>. L'utilisateur devra le changer à la
                  prochaine connexion.
                </p>
                <div>
                  <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
                    Nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value)
                      setPwError('')
                    }}
                    className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
                    placeholder="Min. 8 caractères, 1 majuscule, 1 chiffre"
                  />
                  {pwError && <p className="text-xs text-[#DC2626] mt-1">{pwError}</p>}
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => {
                      setShowResetModal(false)
                      setNewPassword('')
                      setPwError('')
                    }}
                    className="flex-1 px-4 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => resetPassword.mutate()}
                    disabled={resetPassword.isPending || !newPassword}
                    className="flex-1 px-4 py-2 bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium hover:bg-[var(--blue-d)] transition-colors disabled:opacity-50"
                  >
                    {resetPassword.isPending ? '…' : 'Confirmer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal — Supprimer compte */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-[rgba(22,12,38,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
              <div
                className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-4"
                style={{ boxShadow: '0 24px 64px rgba(13,8,32,0.18)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FEF2F2] rounded-xl flex items-center justify-center text-lg">
                    ⚠️
                  </div>
                  <h3 className="text-base font-bold text-[var(--brand-dark)]">
                    Supprimer le compte utilisateur
                  </h3>
                </div>
                <p className="text-sm text-[var(--tx2)]">
                  Le compte de <strong>{linkedUser?.email}</strong> sera définitivement supprimé.
                  <br />
                  <span className="text-xs text-[var(--tx3)] mt-1 block">
                    La fiche employé RH et l'historique sont conservés.
                  </span>
                </p>
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => deleteUserAccount.mutate()}
                    disabled={deleteUserAccount.isPending}
                    className="flex-1 px-4 py-2 bg-[#DC2626] text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {deleteUserAccount.isPending ? '…' : 'Supprimer définitivement'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal global — Supprimer employé */}
      {showDeleteEmpModal && (
        <div className="fixed inset-0 bg-[rgba(22,12,38,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div
            className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-5"
            style={{ boxShadow: '0 24px 64px rgba(13,8,32,0.2)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FEF2F2] rounded-xl flex items-center justify-center text-lg shrink-0">
                🗑️
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--brand-dark)]">Suppression définitive de l'employé</h3>
                <p className="text-xs text-[var(--tx3)] mt-0.5">
                  {employee?.matricule} · {employee?.firstName} {employee?.lastName}
                </p>
              </div>
            </div>

            <div className="p-4 bg-[#FEF2F2] border border-red-200 rounded-xl space-y-2">
              <p className="text-sm font-semibold text-[#DC2626]">
                ⚠️ Suppression DÉFINITIVE — réservée au super-administrateur
              </p>
              <p className="text-sm text-[var(--tx2)]">
                Ne faites cela <strong>que si cet employé a été créé par erreur ou durant des tests</strong>.
                Cette action est irréversible et a de nombreuses répercussions système :
              </p>
              <ul className="text-xs text-[var(--tx2)] list-disc pl-4 space-y-0.5">
                <li>Contrats et avenants, bulletins de paie (y compris calculés ou scellés), avances et commissions</li>
                <li>Demandes et soldes de congés, pointages, évaluations, formations, historique de carrière</li>
                <li>Documents RH, poste d'organigramme (rendu vacant), rattachements manager / département</li>
                <li>
                  Le compte utilisateur lié
                  {((linkedUser as any)?.email ?? employee?.email) ? <> ({(linkedUser as any)?.email ?? employee?.email})</> : null}
                  {' '}est supprimé — ou désactivé s'il a créé du contenu (messages, tickets…)
                </li>
              </ul>
              <p className="text-xs text-[var(--tx3)]">
                Les archives légales déjà scellées (WORM) ne sont jamais effacées mais deviennent orphelines.
                Pour un départ réel, utilisez la fin de contrat (offboarding), qui conserve l'historique.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
                Pour confirmer, saisissez le matricule{' '}
                <span className="font-mono text-[var(--brand-dark)]">{employee?.matricule}</span>
              </label>
              <input
                value={confirmMatricule}
                onChange={(e) => setConfirmMatricule(e.target.value)}
                placeholder={employee?.matricule}
                className="w-full px-3 py-2 bg-white border border-red-200 rounded-lg text-sm font-mono text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[#DC2626]"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowDeleteEmpModal(false)}
                className="flex-1 px-4 py-2.5 border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => deleteEmployee.mutate()}
                disabled={deleteEmployee.isPending || confirmMatricule.trim() !== employee?.matricule}
                className="flex-1 px-4 py-2.5 bg-[#DC2626] text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteEmployee.isPending ? '…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'career' && (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-px bg-[var(--bd)]" />
          <div className="space-y-4 ml-10">
            {(history ?? []).map((event, i) => {
              const ICONS: Record<string, React.ElementType> = {
                HIRE: UserCheck,
                PROMOTION: TrendingUp,
                TRANSFER: Building2,
                SALARY_CHANGE: FileText,
                TERMINATION: UserX,
              }
              const Icon = ICONS[event.type] ?? Award
              return (
                <div key={event.id} className="relative">
                  <div className="absolute -left-10 w-4 h-4 rounded-full bg-[var(--surface-2)] border-2 border-[var(--blue)] flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#38BDF8]" />
                  </div>
                  <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm font-medium text-[var(--brand-dark)]">{event.description}</p>
                      <span className="text-xs text-[var(--tx3)]">{fmtDate(event.effectiveDate)}</span>
                    </div>
                    {(event.fromJobTitle || event.toJobTitle) && (
                      <p className="text-xs text-[var(--tx3)]">
                        {event.fromJobTitle} → {event.toJobTitle}
                      </p>
                    )}
                    {(event.fromSalary || event.toSalary) && (
                      <p className="text-xs text-[var(--tx3)]">
                        {event.fromSalary && fmtXOF(event.fromSalary)} →{' '}
                        {event.toSalary && fmtXOF(event.toSalary)}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
            {!history?.length && (
              <div className="flex items-center justify-center h-32 text-[var(--tx3)] text-sm">
                Aucun événement carrière
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  EMPLOYEE CREATE PAGE
// ============================================================================

export function EmployeeCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    departmentId: '',
    managerId: '',
    hireDate: new Date().toISOString().split('T')[0],
    ipresId: '',
    // [Payroll] Situation familiale
    maritalStatus: 'CELIBATAIRE' as 'CELIBATAIRE' | 'MARIE' | 'DIVORCE' | 'VEUF' | 'UNION_LIBRE',
    spouseEmployed: false as boolean,
    dependentChildren: 0 as number,
    otherDependents: 0 as number,
  })
  const [user, setUser] = useState({
    createUser: true,
    mode: 'new' as 'new' | 'link', // new = créer compte | link = lier existant
    roleId: '',
    existingUserId: '',
  })
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }))
  const setU = (k: string, v: any) => setUser((f) => ({ ...f, [k]: v }))

  const { data: depts } = useQuery({
    queryKey: ['departments'],
    queryFn: () => hrApi.employees.departments(),
  })
  const { data: rolesData } = useQuery({
    queryKey: ['admin-roles-list'],
    queryFn: () => adminApi.getRoles({ page: 1, limit: 100 }),
    enabled: user.createUser && user.mode === 'new',
  })
  const { data: usersData } = useQuery({
    queryKey: ['admin-users-unlinked'],
    queryFn: () => usersApi.directory({ limit: 100 }),
    enabled: user.createUser && user.mode === 'link',
  })

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      // Étape 1 — Créer l'employé (champs FK vides → undefined pour éviter P2003)
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? undefined : v]),
      )
      const emp = await hrApi.employees.create(payload)

      // Étape 2 — Gérer le compte utilisateur
      if (user.createUser) {
        if (user.mode === 'new' && user.roleId) {
          // Inviter → récupérer userId → lier à l'employé
          const inviteRes = await adminApi.inviteUser({
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            roleId: user.roleId,
            phone: form.phone || undefined,
            createEmployee: false,
          } as any)
          // inviteRes = { success, data: { userId, message, ... }, timestamp }
          const newUserId = (inviteRes as any)?.data?.userId ?? (inviteRes as any)?.userId
          if (newUserId) {
            await hrApi.employees.update(emp.id, { userId: newUserId })
          }
        } else if (user.mode === 'link' && user.existingUserId) {
          // Lier à un compte existant directement
          await hrApi.employees.update(emp.id, { userId: user.existingUserId })
        }
      }

      return emp
    },
    onSuccess: (emp) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      const baseMsg =
        user.createUser && user.mode === 'new' && user.roleId
          ? `Matricule ${emp.matricule} généré. Invitation envoyée à ${form.email}.`
          : `Matricule ${emp.matricule} généré.`
      const msg = `${baseMsg} Prochaine étape : créer le contrat dans la fiche employé.`
      addNotification({ type: 'success', title: 'Employé créé', message: msg })
      // Rediriger vers la fiche avec un flag d'ouverture auto du modal contrat
      navigate(`/hr/employees/${emp.id}?openContract=1`)
    },
    onError: (err: any) =>
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: err.response?.data?.message ?? 'Erreur création employé',
      }),
  })

  const inputCls =
    'w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]/40 focus:border-[var(--blue)]'
  const labelCls = 'block text-xs font-medium text-[var(--tx3)] mb-1.5'
  const sectionCls = 'bg-white border border-[var(--bd)] rounded-xl p-5 space-y-4'

  return (
    <div className="space-y-5 w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
        </button>
        <div>
          <h1
            style={{
              fontFamily: 'Raleway,sans-serif',
              fontWeight: 800,
              fontSize: 22,
              color: 'var(--brand-dark)',
              margin: 0,
            }}
          >
            Nouvel employé
          </h1>
          <p className="text-xs text-[var(--tx3)] mt-0.5">
            Créez la fiche RH et le compte utilisateur en une seule étape
          </p>
        </div>
      </div>

      {/* ── Section 1 : Identité ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">Identité</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>
              Prénom <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              className={inputCls}
              placeholder="Amara"
            />
          </div>
          <div>
            <label className={labelCls}>
              Nom <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              className={inputCls}
              placeholder="Diallo"
            />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>
              Email professionnel <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className={inputCls}
              placeholder="amara.diallo@nexuscorp.sn"
            />
          </div>
          <div>
            <label className={labelCls}>Téléphone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className={inputCls}
              placeholder="+221 77 000 00 00"
            />
          </div>
        </div>
      </div>

      {/* ── Section 2 : Poste ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
          Poste & Affectation
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>
              Intitulé du poste <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={form.jobTitle}
              onChange={(e) => set('jobTitle', e.target.value)}
              className={inputCls}
              placeholder="ex : Développeur Senior, Responsable RH…"
            />
          </div>
          <div>
            <label className={labelCls}>Département</label>
            <select
              value={form.departmentId}
              onChange={(e) => set('departmentId', e.target.value)}
              className={inputCls}
            >
              <option value="">Sélectionner…</option>
              {depts?.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.code ? ` (${d.code})` : ''}
                </option>
              ))}
            </select>
            {(!depts || depts.length === 0) && (
              <p className="text-xs text-[#F59E0B] mt-1">
                ⚠ Aucun département — relancez le seed ou créez-en un
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>
              Date d'embauche <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="date"
              value={form.hireDate}
              onChange={(e) => set('hireDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>N° IPRES</label>
            <input
              type="text"
              value={form.ipresId}
              onChange={(e) => set('ipresId', e.target.value)}
              className={inputCls}
              placeholder="Numéro IPRES retraite"
            />
          </div>
        </div>
      </div>

      {/* ── Section : Situation familiale (paie) ── */}
      <div className={sectionCls}>
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
            Situation familiale
          </p>
          <span className="text-[10px] px-2 py-0.5 bg-[#EDE9FE] text-[#7C3AED] rounded font-medium">
            Impacte le calcul IRPP
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Statut matrimonial</label>
            <select
              value={form.maritalStatus}
              onChange={(e) => set('maritalStatus', e.target.value)}
              className={inputCls}
            >
              <option value="CELIBATAIRE">Célibataire</option>
              <option value="MARIE">Marié(e)</option>
              <option value="DIVORCE">Divorcé(e)</option>
              <option value="VEUF">Veuf/Veuve</option>
              <option value="UNION_LIBRE">Union libre</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Nombre d'enfants à charge</label>
            <input
              type="number"
              min={0}
              max={20}
              value={form.dependentChildren}
              onChange={(e) => set('dependentChildren', parseInt(e.target.value) || 0)}
              className={inputCls}
            />
          </div>
          {form.maritalStatus === 'MARIE' && (
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-sm text-[var(--tx2)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.spouseEmployed}
                  onChange={(e) => set('spouseEmployed', e.target.checked)}
                  className="w-4 h-4"
                />
                Conjoint(e) salarié(e)
                <span className="text-xs text-[var(--tx3)]">— Si coché, l'avantage matrimonial est divisé par 2</span>
              </label>
            </div>
          )}
          <div className="col-span-2 bg-[#F0FDF4] border border-[#10B981]/25 rounded-lg p-3 text-xs text-[#059669]">
            <strong>📊 Parts fiscales</strong> seront calculées automatiquement selon le CGI SN art. 174 (max 5 parts).
            Le moteur de paie utilise ces parts pour appliquer la réduction familiale sur l'IRPP.
          </div>
        </div>
      </div>

      {/* ── Section 3 : Compte utilisateur ── */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
              Compte utilisateur
            </p>
            <p className="text-xs text-[var(--tx3)] mt-0.5">Accès à la plateforme NexusCorp</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setU('createUser', !user.createUser)}
              style={{
                width: 40,
                height: 22,
                borderRadius: 99,
                cursor: 'pointer',
                transition: 'background .2s',
                background: user.createUser ? 'var(--brand-orange)' : 'var(--bd)',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 3,
                  left: user.createUser ? 20 : 3,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left .2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
            </div>
            <span className="text-xs font-medium text-[var(--tx2)]">
              {user.createUser ? 'Activé' : 'Sans accès plateforme'}
            </span>
          </label>
        </div>

        {user.createUser && (
          <div className="space-y-4">
            {/* Toggle mode new/link */}
            <div className="flex gap-2">
              {(['new', 'link'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setU('mode', mode)}
                  className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors border"
                  style={{
                    background: user.mode === mode ? '#EFF6FF' : 'var(--surface-2)',
                    borderColor: user.mode === mode ? 'rgba(245,124,0,0.4)' : 'var(--bd)',
                    color: user.mode === mode ? 'var(--blue-d)' : 'var(--tx3)',
                  }}
                >
                  {mode === 'new' ? '✉ Créer et inviter par email' : '🔗 Lier à un compte existant'}
                </button>
              ))}
            </div>

            {/* Mode : créer nouveau compte */}
            {user.mode === 'new' && (
              <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3">
                <p className="text-xs text-[var(--tx3)]">
                  Un lien d'activation valable 72h sera envoyé à{' '}
                  <strong className="text-[var(--tx2)]">{form.email || '…'}</strong>
                </p>
                <div>
                  <label className={labelCls}>
                    Rôle <span className="text-[#DC2626]">*</span>
                  </label>
                  <select
                    value={user.roleId}
                    onChange={(e) => setU('roleId', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Sélectionner un rôle…</option>
                    {rolesData?.data?.map((r: any) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Mode : lier compte existant */}
            {user.mode === 'link' && (
              <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3">
                <p className="text-xs text-[var(--tx3)]">
                  Sélectionnez un utilisateur existant sans fiche employé
                </p>
                <div>
                  <label className={labelCls}>Utilisateur existant</label>
                  <select
                    value={user.existingUserId}
                    onChange={(e) => setU('existingUserId', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Sélectionner…</option>
                    {usersData?.map((u: any) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} — {u.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Actions ── */}
      <div className="flex items-center justify-end gap-3 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={() => mutate()}
          disabled={
            isPending ||
            !form.firstName ||
            !form.lastName ||
            !form.email ||
            !form.jobTitle ||
            (user.createUser && user.mode === 'new' && !user.roleId) ||
            (user.createUser && user.mode === 'link' && !user.existingUserId)
          }
          className="flex items-center gap-2 px-6 py-2.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: 'Raleway,sans-serif' }}
        >
          {isPending ? (
            <div className="w-4 h-4 border-2 border-[var(--tx3)] border-t-white rounded-full animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          {user.createUser && user.mode === 'new' && user.roleId
            ? 'Créer + Inviter'
            : user.createUser && user.mode === 'link' && user.existingUserId
              ? 'Créer + Lier'
              : "Créer l'employé"}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
//  EMPLOYEE EDIT PAGE
// ============================================================================

export function EmployeeEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => hrApi.employees.get(id!),
    enabled: !!id,
  })

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    departmentId: '',
    managerId: '',
    hireDate: '',
    ipresId: '',
    ninea: '',
    address: '',
    city: '',
    nationality: '',
    nationalId: '',
    gender: '',
    birthDate: '',
    notes: '',
    attendanceRuleId: '',
    // [Payroll]
    maritalStatus: 'CELIBATAIRE' as 'CELIBATAIRE' | 'MARIE' | 'DIVORCE' | 'VEUF' | 'UNION_LIBRE',
    spouseEmployed: false as boolean,
    dependentChildren: 0 as number,
    otherDependents: 0 as number,
  })
  const [initialized, setInitialized] = useState(false)

  // Pré-remplir le formulaire une fois l'employé chargé
  if (employee && !initialized) {
    setForm({
      firstName: employee.firstName ?? '',
      lastName: employee.lastName ?? '',
      email: employee.email ?? '',
      phone: employee.phone ?? '',
      jobTitle: employee.jobTitle ?? '',
      departmentId: employee.departmentId ?? '',
      managerId: employee.managerId ?? '',
      hireDate: employee.hireDate ? new Date(employee.hireDate).toISOString().split('T')[0] : '',
      ipresId: employee.ipresId ?? '',
      ninea: employee.ninea ?? '',
      address: employee.address ?? '',
      city: employee.city ?? '',
      nationality: employee.nationality ?? '',
      nationalId: employee.nationalId ?? '',
      gender: employee.gender ?? '',
      birthDate: employee.birthDate ? new Date(employee.birthDate).toISOString().split('T')[0] : '',
      notes: employee.notes ?? '',
      attendanceRuleId: (employee as any).attendanceRuleId ?? '',
      // [Payroll]
      maritalStatus:     (employee.maritalStatus     as any) ?? 'CELIBATAIRE',
      spouseEmployed:    employee.spouseEmployed    ?? false,
      dependentChildren: employee.dependentChildren ?? 0,
      otherDependents:   employee.otherDependents   ?? 0,
    })
    setInitialized(true)
  }

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }))

  const { data: depts } = useQuery({
    queryKey: ['departments'],
    queryFn: () => hrApi.employees.departments(),
  })
  const { data: managers } = useQuery({
    queryKey: ['employees-managers'],
    queryFn: () => hrApi.employees.list({ limit: 100 }),
  })
  const { data: attendanceRules } = useQuery({
    queryKey: ['attendance-rules'],
    queryFn: () => hrApi.attendance.getRules(),
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const payload: any = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? undefined : v]),
      )
      // Règle de pointage : '' = détacher (null) explicitement, pas « inchangé ».
      payload.attendanceRuleId = form.attendanceRuleId || null
      return hrApi.employees.update(id!, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', id] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      addNotification({
        type: 'success',
        title: 'Employé mis à jour',
        message: 'Les informations ont été sauvegardées.',
      })
      navigate(`/hr/employees/${id}`)
    },
    onError: (err: any) =>
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: err.response?.data?.message ?? 'Erreur lors de la mise à jour',
      }),
  })

  const inputCls =
    'w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]/40 focus:border-[var(--blue)]'
  const labelCls = 'block text-xs font-medium text-[var(--tx3)] mb-1.5'
  const sectionCls = 'bg-white border border-[var(--bd)] rounded-xl p-5 space-y-4'

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
      </div>
    )

  if (!employee) return <div className="text-center py-10 text-[var(--tx3)]">Employé introuvable.</div>

  return (
    <div className="space-y-5 w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
        </button>
        <div>
          <h1
            style={{
              fontFamily: 'Raleway,sans-serif',
              fontWeight: 800,
              fontSize: 22,
              color: 'var(--brand-dark)',
              margin: 0,
            }}
          >
            Modifier — {employee.firstName} {employee.lastName}
          </h1>
          <p className="text-xs text-[var(--tx3)] mt-0.5">
            {employee.matricule} · {employee.jobTitle}
          </p>
        </div>
      </div>

      {/* ── Section 1 : Identité ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">Identité</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>
              Prénom <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>
              Nom <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>
              Email professionnel <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Téléphone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className={inputCls}
              placeholder="+221 77 000 00 00"
            />
          </div>
          <div>
            <label className={labelCls}>Genre</label>
            <select
              value={form.gender}
              onChange={(e) => set('gender', e.target.value)}
              className={inputCls}
            >
              <option value="">Sélectionner…</option>
              <option value="MALE">Homme</option>
              <option value="FEMALE">Femme</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Date de naissance</label>
            <input
              type="date"
              value={form.birthDate}
              onChange={(e) => set('birthDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Nationalité</label>
            <input
              type="text"
              value={form.nationality}
              onChange={(e) => set('nationality', e.target.value)}
              className={inputCls}
              placeholder="Sénégalaise"
            />
          </div>
          <div>
            <label className={labelCls}>N° CNI / Passeport</label>
            <input
              type="text"
              value={form.nationalId}
              onChange={(e) => set('nationalId', e.target.value)}
              className={inputCls}
              placeholder="N° pièce d'identité"
            />
          </div>
        </div>
      </div>

      {/* ── Section 2 : Poste & Affectation ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
          Poste & Affectation
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>
              Intitulé du poste <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="text"
              value={form.jobTitle}
              onChange={(e) => set('jobTitle', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Département</label>
            <select
              value={form.departmentId}
              onChange={(e) => set('departmentId', e.target.value)}
              className={inputCls}
            >
              <option value="">Sélectionner…</option>
              {depts?.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.code ? ` (${d.code})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Manager direct</label>
            <select
              value={form.managerId}
              onChange={(e) => set('managerId', e.target.value)}
              className={inputCls}
            >
              <option value="">Aucun</option>
              {(managers as any)?.employees
                ?.filter((e: any) => e.id !== id)
                .map((e: any) => (
                  <option key={e.id} value={e.id}>
                    {e.firstName} {e.lastName} — {e.jobTitle}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>
              Date d'embauche <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="date"
              value={form.hireDate}
              onChange={(e) => set('hireDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Règle de pointage</label>
            <select
              value={form.attendanceRuleId}
              onChange={(e) => set('attendanceRuleId', e.target.value)}
              className={inputCls}
            >
              <option value="">Règle par défaut de l'entité</option>
              {(attendanceRules ?? []).map((r: any) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.workStartTime}–{r.workEndTime}){r.isDefault ? ' — défaut' : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[var(--tx3)] mt-1">Détermine les horaires servant au calcul des retards.</p>
          </div>
        </div>
      </div>

      {/* ── Section 3 : Informations sociales ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
          Informations sociales
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>N° IPRES</label>
            <input
              type="text"
              value={form.ipresId}
              onChange={(e) => set('ipresId', e.target.value)}
              className={inputCls}
              placeholder="Numéro IPRES retraite"
            />
          </div>
          <div>
            <label className={labelCls}>NINEA (si applicable)</label>
            <input
              type="text"
              value={form.ninea}
              onChange={(e) => set('ninea', e.target.value)}
              className={inputCls}
              placeholder="ex : 1234567AB"
            />
          </div>
        </div>
      </div>

      {/* ── Section : Situation familiale (Paie) ── */}
      <div className={sectionCls}>
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
            Situation familiale
          </p>
          <span className="text-[10px] px-2 py-0.5 bg-[#EDE9FE] text-[#7C3AED] rounded font-medium">
            Impacte le calcul IRPP
          </span>
          {employee?.fiscalParts != null && (
            <span className="text-[10px] px-2 py-0.5 bg-[var(--sb-bg)] text-white rounded font-bold">
              {employee.fiscalParts} part{employee.fiscalParts > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Statut matrimonial</label>
            <select
              value={form.maritalStatus}
              onChange={(e) => set('maritalStatus', e.target.value)}
              className={inputCls}
            >
              <option value="CELIBATAIRE">Célibataire</option>
              <option value="MARIE">Marié(e)</option>
              <option value="DIVORCE">Divorcé(e)</option>
              <option value="VEUF">Veuf/Veuve</option>
              <option value="UNION_LIBRE">Union libre</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Enfants à charge</label>
            <input
              type="number"
              min={0}
              max={20}
              value={form.dependentChildren}
              onChange={(e) => set('dependentChildren', parseInt(e.target.value) || 0)}
              className={inputCls}
            />
          </div>
          {form.maritalStatus === 'MARIE' && (
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-sm text-[var(--tx2)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.spouseEmployed}
                  onChange={(e) => set('spouseEmployed', e.target.checked)}
                  className="w-4 h-4"
                />
                Conjoint(e) salarié(e)
              </label>
            </div>
          )}
          <div className="col-span-2">
            <label className={labelCls}>Autres personnes à charge</label>
            <input
              type="number"
              min={0}
              max={10}
              value={form.otherDependents}
              onChange={(e) => set('otherDependents', parseInt(e.target.value) || 0)}
              className={inputCls}
              placeholder="Parents, ascendants à charge…"
            />
          </div>
          <div className="col-span-2 bg-[#F0FDF4] border border-[#10B981]/25 rounded-lg p-3 text-xs text-[#059669]">
            Les <strong>parts fiscales</strong> seront recalculées automatiquement (CGI SN art. 174, max 5 parts).
          </div>
        </div>
      </div>

      {/* ── Section 4 : Adresse ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">Adresse</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>Adresse</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              className={inputCls}
              placeholder="Rue, quartier…"
            />
          </div>
          <div>
            <label className={labelCls}>Ville</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              className={inputCls}
              placeholder="Dakar"
            />
          </div>
        </div>
      </div>

      {/* ── Section 5 : Notes ── */}
      <div className={sectionCls}>
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">Notes internes</p>
        <textarea
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={3}
          className={inputCls + ' resize-none'}
          placeholder="Observations, informations complémentaires…"
        />
      </div>

      {/* ── Actions ── */}
      <div className="flex items-center justify-between pb-6">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={() => mutate()}
          disabled={isPending || !form.firstName || !form.lastName || !form.email || !form.jobTitle}
          className="flex items-center gap-2 px-6 py-2.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: 'Raleway,sans-serif' }}
        >
          {isPending ? (
            <div className="w-4 h-4 border-2 border-[var(--tx3)] border-t-white rounded-full animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
          Sauvegarder les modifications
        </button>
      </div>
    </div>
  )
}
